import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { ReportShell } from "@/features/reports/ReportShell";
import { useDisposalRegister, type DisposalRegisterRow } from "@/features/disposals/api";
import { DISPOSAL_MODES, formatINR } from "@/features/disposals/types";
import { CertificateLink } from "@/features/disposals/DisposalSummaryCard";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";

export const Route = createFileRoute("/_authenticated/reports/disposal-register")({
  component: DisposalRegisterReport,
});

function fmtDate(d: string | null | undefined): string {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function shortDisposalNo(id: string): string {
  return `DSP-${id.slice(0, 8).toUpperCase()}`;
}

function DisposalRegisterReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [mode, setMode] = useState<string>("all");
  const [buyer, setBuyer] = useState("");
  const { currentBranchId } = useBranch();
  const { data: rows = [], isLoading } = useDisposalRegister(currentBranchId);

  const filtered = useMemo<DisposalRegisterRow[]>(() => {
    return rows.filter((r) => {
      if (filters.departmentId && r.department_id !== filters.departmentId) return false;
      if (filters.categoryId && r.category_id !== filters.categoryId) return false;
      if (mode !== "all" && r.mode !== mode) return false;
      if (buyer.trim() && !(r.buyer_name ?? "").toLowerCase().includes(buyer.trim().toLowerCase())) return false;
      if (filters.dateFrom && (r.disposal_date ?? "") < filters.dateFrom) return false;
      if (filters.dateTo && (r.disposal_date ?? "") > filters.dateTo) return false;
      return true;
    });
  }, [rows, filters, mode, buyer]);

  const exportRows = useMemo(() => () => filtered.map((r) => ({
    DisposalNo: shortDisposalNo(r.id),
    AssetCode: r.asset_code ?? "",
    AssetName: r.asset_name ?? "",
    Department: r.department_name ?? "",
    Location: r.location_name ?? "",
    Category: r.category_name ?? "",
    CondemnationRequestNo: r.request_no ?? "",
    DisposalMode: r.mode,
    Buyer: r.buyer_name ?? "",
    SaleValue: r.sale_value ?? 0,
    GST: r.gst_on_sale ?? 0,
    DisposalDate: r.disposal_date,
    DisposedBy: r.disposed_by_name ?? "",
    Certificate: r.certificate_url ?? "",
    Remarks: r.remarks ?? "",
  })), [filtered]);

  return (
    <ReportShell id="R-15" title="Disposal Register"
      description="Completed disposals with buyer, sale value, GST and certificate."
      filters={filters} onChange={setFilters}
      enabled={["departmentId", "categoryId", "dateFrom", "dateTo"]}
      exportRows={exportRows}
      rightActions={
        <div className="flex flex-wrap items-center gap-2">
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="w-44"><SelectValue placeholder="Mode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modes</SelectItem>
              {DISPOSAL_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="Buyer / Recycler / Agency" className="w-56" />
        </div>
      }>
      <Card><CardContent className="p-0 overflow-x-auto">
        {isLoading ? (
          <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Disposal No</TableHead>
              <TableHead>Asset Code</TableHead>
              <TableHead>Asset Name</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Condemnation</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Counterparty</TableHead>
              <TableHead className="text-right">Sale Value</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead>Disposal Date</TableHead>
              <TableHead>Certificate</TableHead>
              <TableHead>Remarks</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.slice(0, 500).map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono text-xs">
                    <Link to="/disposals" className="text-primary hover:underline">{shortDisposalNo(r.id)}</Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.asset_id ? (
                      <Link to="/assets/$assetId" params={{ assetId: r.asset_id }} className="text-primary hover:underline">{r.asset_code}</Link>
                    ) : r.asset_code}
                  </TableCell>
                  <TableCell>{r.asset_name ?? "—"}</TableCell>
                  <TableCell>{r.department_name ?? "—"}</TableCell>
                  <TableCell>{r.location_name ?? "—"}</TableCell>
                  <TableCell>{r.category_name ?? "—"}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.condemnation_id ? (
                      <Link to="/condemnation/$id" params={{ id: r.condemnation_id }} className="text-primary hover:underline">{r.request_no ?? "—"}</Link>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-xs">{r.mode}</TableCell>
                  <TableCell>{r.buyer_name ?? "—"}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatINR(r.sale_value)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatINR(r.gst_on_sale)}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(r.disposal_date)}</TableCell>
                  <TableCell><CertificateLink url={r.certificate_url} /></TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate" title={r.remarks ?? undefined}>
                    {r.remarks || "—"}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={14} className="py-8 text-center text-sm text-muted-foreground">No disposal records match the current filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
        <div className="border-t p-3 text-xs text-muted-foreground">
          {filtered.length > 500 ? `Showing first 500 of ${filtered.length}. Export CSV for full list.` : `Showing ${filtered.length} of ${filtered.length}.`}
        </div>
      </CardContent></Card>
    </ReportShell>
  );
}
